/**
 * ActionsTab Component
 * Tab content for selecting an action in AddStepDialog.
 * Shows a searchable grid of actions with a "New" button.
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ImageOutlined as ImageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action } from '../../../types/action';
import { ActionEditor } from '../../ActionEditor';

interface ActionsTabProps {
  actions: Action[];
  error?: boolean; // loading the actions failed: say so, never 'No Actions Yet' (BA-145)
  deviceId: string;
  onSelect: (action: Action) => void;
  onActionCreated?: () => void;
}

export const ActionsTab: React.FC<ActionsTabProps> = ({
  actions,
  error = false,
  deviceId,
  onSelect,
  onActionCreated,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);

  // Filter actions by search query
  const filteredActions = useMemo(() => {
    if (!searchQuery.trim()) return actions;
    const query = searchQuery.toLowerCase();
    return actions.filter(
      (action) =>
        action.name.toLowerCase().includes(query) ||
        action.description.toLowerCase().includes(query)
    );
  }, [actions, searchQuery]);

  const handleNewAction = () => {
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
  };

  const handleActionSaved = () => {
    setEditorOpen(false);
    onActionCreated?.();
  };

  return (
    <Box>
      {/* New button and search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleNewAction}
        >
          {t('schedules.addStep.newAction')}
        </Button>
        <TextField
          fullWidth
          size="small"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Empty state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('actions.pickerLoadFailed')}
        </Alert>
      )}

      {!error && actions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('actions.noActionsYet')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewAction}
            sx={{ mt: 2 }}
          >
            {t('schedules.addStep.newAction')}
          </Button>
        </Box>
      )}

      {/* No matches */}
      {actions.length > 0 && filteredActions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('common.noResults')}
          </Typography>
        </Box>
      )}

      {/* Actions grid */}
      {filteredActions.length > 0 && (
        <Grid container spacing={2}>
          {filteredActions.map((action) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={action.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                }}
              >
                <CardActionArea
                  onClick={() => onSelect(action)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  {action.thumbnailUrl || action.pictureUrl ? (
                    <CardMedia
                      component="img"
                      height="100"
                      image={action.thumbnailUrl || action.pictureUrl || ''}
                      alt={action.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1, py: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      noWrap
                      title={action.name}
                    >
                      {action.name}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Editor Dialog */}
      <ActionEditor
        open={editorOpen}
        onClose={handleEditorClose}
        onSave={handleActionSaved}
        deviceId={deviceId}
        action={null}
      />
    </Box>
  );
};
